import { useMemo } from "react";

import { styled } from "@linaria/react";

import { withTheme, type PropsWithTheme } from "@gx/dls/theme";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import { ALL_SOURCES } from "@/pages/sources/components/create/constants";
import CreateSourceSelectorCard from "@/pages/sources/components/create/select/CreateSourceSelectorCard";
import {
  SOURCE_TYPE_TO_DESCRIPTION_MAP,
  SOURCE_TYPE_TO_LABEL_MAP,
} from "@/pages/sources/constants";
import { useSourceTypesEnabled } from "@/pages/sources/hooks/useSourceTypesEnabled";

interface CreateSourceSelectorBodyProps {
  search: string;
  onSelect: (sourceType: SourceType) => void;
}

const BodyWrapper = withTheme(styled.div<PropsWithTheme>`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.base};
  border-radius: 0 0 8px 8px;
  padding: 16px;
`);

const GridWrapper = styled.div<{ $itemCount: number }>`
  display: grid;
  grid-template-columns: ${({ $itemCount }) =>
    $itemCount <= 2 ? `repeat(${$itemCount}, 1fr)` : "repeat(3, 1fr)"};
  gap: 12px;
`;

const CreateSourceSelectorBody = ({ search, onSelect }: CreateSourceSelectorBodyProps) => {
  const sourceTypesEnabled = useSourceTypesEnabled();

  const filteredAndSortedSourceTypes = useMemo(() => {
    const searchLower = search.toLowerCase();

    // Filter by search
    const filtered = ALL_SOURCES.filter((sourceType) => {
      // UNSPECIFIED always shows (it's the "other" card)
      if (sourceType === SourceType.UNSPECIFIED) {
        return true;
      }
      const label = SOURCE_TYPE_TO_LABEL_MAP[sourceType].toLowerCase();
      const description = SOURCE_TYPE_TO_DESCRIPTION_MAP[sourceType].toLowerCase();
      return label.includes(searchLower) || description.includes(searchLower);
    });

    // Sort: enabled first (alphabetically), then disabled (alphabetically), UNSPECIFIED last
    return filtered.sort((a, b) => {
      // UNSPECIFIED always goes last
      if (a === SourceType.UNSPECIFIED) return 1;
      if (b === SourceType.UNSPECIFIED) return -1;

      const aEnabled = sourceTypesEnabled[a];
      const bEnabled = sourceTypesEnabled[b];

      // If both have same enabled status, sort alphabetically
      if (aEnabled === bEnabled) {
        const labelA = SOURCE_TYPE_TO_LABEL_MAP[a];
        const labelB = SOURCE_TYPE_TO_LABEL_MAP[b];
        return labelA.localeCompare(labelB);
      }

      // Enabled sources come first
      return aEnabled ? -1 : 1;
    });
  }, [search, sourceTypesEnabled]);

  return (
    <BodyWrapper>
      <GridWrapper $itemCount={filteredAndSortedSourceTypes.length}>
        {filteredAndSortedSourceTypes.map((sourceType) => (
          <CreateSourceSelectorCard key={sourceType} sourceType={sourceType} onSelect={onSelect} />
        ))}
      </GridWrapper>
    </BodyWrapper>
  );
};

export default CreateSourceSelectorBody;
